import React, { createContext, useContext, useState, useMemo, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useColorScheme } from 'react-native';
import { darkTheme, lightTheme, type Theme, type ThemeMode } from './theme';
import { loadThemePreference, saveThemePreference } from './themePreferenceStorage';

/**
 * The user's stored preference. `'system'` defers to the OS-level dark/light
 * setting via React Native's `useColorScheme()`. The other two are explicit
 * overrides chosen via the toggle in Profile → Appearance.
 */
export type ThemePreference = ThemeMode | 'system';

interface ThemeContextValue {
  /** The actively-rendered theme object — already resolved from preference + OS. */
  theme: Theme;
  /** Whether the active theme is `'light'` or `'dark'` after resolution. */
  activeMode: ThemeMode;
  /** The user's stored preference (what the toggle UI should show as selected). */
  preference: ThemePreference;
  /** Update the preference. Persists to AsyncStorage best-effort. */
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children: ReactNode;
  /**
   * Seed value used for the very first render before AsyncStorage has been
   * read. The stored preference replaces this once it loads (typically a
   * single frame later). Most callers should leave this at the default.
   */
  defaultPreference?: ThemePreference;
}

export function ThemeProvider({ children, defaultPreference = 'system' }: ThemeProviderProps) {
  const systemScheme = useColorScheme(); // 'light' | 'dark' | null
  const [preference, setPreferenceState] = useState<ThemePreference>(defaultPreference);

  // Tracks whether the user has explicitly chosen a preference during this
  // session. If they toggle the Appearance control before the AsyncStorage
  // read resolves, the load handler must NOT overwrite that choice with
  // the (now-stale) stored value.
  const userInteractedRef = useRef(false);

  // Load the stored preference on mount. We don't gate the first render
  // behind this — the brief flicker between defaultPreference and the
  // stored value is preferable to a blank screen, and on most launches
  // the AsyncStorage read resolves before the first paint.
  useEffect(() => {
    let cancelled = false;
    loadThemePreference().then((loaded) => {
      if (cancelled || userInteractedRef.current) return;
      setPreferenceState(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const setPreference = useCallback((next: ThemePreference) => {
    userInteractedRef.current = true;
    setPreferenceState(next);
    // Fire-and-forget — failures are logged inside the storage helper.
    // Don't await: the toggle UI should feel instant.
    void saveThemePreference(next);
  }, []);

  const value = useMemo<ThemeContextValue>(() => {
    // When preference is 'system' but useColorScheme() returns null (which
    // happens on some platforms during the initial render and on web with
    // no media query support), fall back to dark — the app's historical
    // default. Treating null as 'light' would silently flip the UI on
    // boot for any user whose OS scheme hasn't reported in yet.
    const activeMode: ThemeMode =
      preference === 'system'
        ? systemScheme === 'light'
          ? 'light'
          : 'dark'
        : preference;
    const theme = activeMode === 'dark' ? darkTheme : lightTheme;
    return { theme, activeMode, preference, setPreference };
  }, [preference, systemScheme, setPreference]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Read the active theme. Re-renders when the user toggles the preference
 * or when the OS-level dark/light setting changes (if preference is `'system'`).
 */
export function useTheme(): Theme {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme() must be used within a <ThemeProvider>');
  }
  return ctx.theme;
}

/**
 * Read + update the user's theme preference. Used by the toggle UI in
 * Profile → Appearance. setPreference() persists to AsyncStorage best-effort.
 */
export function useThemePreference() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useThemePreference() must be used within a <ThemeProvider>');
  }
  return {
    preference: ctx.preference,
    activeMode: ctx.activeMode,
    setPreference: ctx.setPreference,
  };
}
